import { useState, useEffect, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import { GameLayout, GameStatusAlert } from '@/components/games/common'
import { GameShortcuts } from '@/components/games/common/game-shortcuts'
import {
  Heart,
  Star,
  Moon,
  Sun,
  Coffee,
  Music,
  Zap,
  CheckCircle,
  Copy
} from 'lucide-react'
import { LucideIcon } from 'lucide-react'
import { saveGameResult, getUserStats, getGameResult, hasPlayedToday } from '@/lib/game-stats'
import { InContentAd } from '@/components/adsense'
import { getDateString } from '@/lib/dictionaries'

interface Card {
  id: number
  iconIndex: number
  isFlipped: boolean
  isMatched: boolean
}

// Tipo para cartas antiguas que pueden tener 'value' en lugar de 'iconIndex'
type LegacyCard = Omit<Card, 'iconIndex'> & {
  iconIndex?: number
  value?: number
}

// Array de iconos para los pares (8 iconos diferentes)
const icons: LucideIcon[] = [
  Heart,
  Star,
  Moon,
  Sun,
  Coffee,
  Music,
  Zap,
  CheckCircle,
]

interface GameState {
  cards: Card[]
  flippedCards: number[]
  moves: number
  matches: number
  gameStatus: 'ready' | 'playing' | 'won' | 'lost'
  isProcessing: boolean
}

const GRID_SIZE = 4 // 4x4 grid = 16 cards = 8 pairs
const MAX_MOVES = 30

// Función para generar las cartas del día
function generateDailyCards(language: string): Card[] {
  const today = new Date()
  const dateString = getDateString(today)

  // Crear seed base
  let seed = 0
  const seedString = dateString + language
  for (let i = 0; i < seedString.length; i++) {
    seed = ((seed << 5) - seed + seedString.charCodeAt(i)) & 0xffffffff
  }

  // Función PRNG simple
  const rng = (function (seed: number) {
    return function () {
      seed = (seed * 9301 + 49297) % 233280
      return seed / 233280
    }
  })(Math.abs(seed))

  // Crear pares de índices de iconos (0-7 para 8 pares)
  const pairs: number[] = []
  for (let i = 0; i < GRID_SIZE * GRID_SIZE / 2; i++) {
    pairs.push(i)
    pairs.push(i)
  }

  // Mezclar usando Fisher-Yates
  for (let i = pairs.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pairs[i], pairs[j]] = [pairs[j], pairs[i]]
  }

  // Crear cartas
  return pairs.map((iconIndex, index) => ({
    id: index,
    iconIndex,
    isFlipped: false,
    isMatched: false
  }))
}

export function MemoriaDePares() {
  const { t, language } = useTranslation()
  const [gameState, setGameState] = useState<GameState>({
    cards: [],
    flippedCards: [],
    moves: 0,
    matches: 0,
    gameStatus: 'playing',
    isProcessing: false
  })

  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  const hasShownWinToastRef = useRef(false)
  const today = getDateString()
  const [stats, setStats] = useState(getUserStats(`memoria-de-pares-${language}`))

  const savePartialGameState = (state: GameState) => {
    if (state.gameStatus === 'playing' || state.gameStatus === 'ready') {
      const partialState = {
        cards: state.cards,
        flippedCards: state.flippedCards,
        moves: state.moves,
        matches: state.matches,
        gameStatus: state.gameStatus,
        date: today
      }
      localStorage.setItem(`game-memoria-de-pares-${language}-partial`, JSON.stringify(partialState))
    }
  }

  const loadPartialGameState = (): Partial<GameState> | null => {
    const key = `game-memoria-de-pares-${language}-partial`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (parsed.date === today) {
          return parsed
        } else {
          localStorage.removeItem(key)
          return null
        }
      } catch {
        return null
      }
    }
    return null
  }

  const clearPartialGameState = () => {
    localStorage.removeItem(`game-memoria-de-pares-${language}-partial`)
  }

  useEffect(() => {
    setIsInitialLoadComplete(false)
    setStats(getUserStats(`memoria-de-pares-${language}`))
    
    // Limpiar estado parcial antiguo que pueda tener 'value' en lugar de 'iconIndex'
    const key = `game-memoria-de-pares-${language}-partial`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (parsed.cards && parsed.cards.length > 0 && parsed.cards[0].value !== undefined && parsed.cards[0].iconIndex === undefined) {
          // Cartas antiguas con 'value', limpiar para regenerar
          localStorage.removeItem(key)
        }
      } catch {
        // Si hay error al parsear, limpiar
        localStorage.removeItem(key)
      }
    }

    if (hasPlayedToday(`memoria-de-pares-${language}`, today)) {
      const todayResult = getGameResult(`memoria-de-pares-${language}`, today)
      if (todayResult) {
        const cards = generateDailyCards(language)
        cards.forEach((card) => {
          if (todayResult.won) {
            card.isMatched = true
            card.isFlipped = true
          }
        })
        setGameState(prev => ({
          ...prev,
          cards,
          gameStatus: todayResult.won ? 'won' : 'lost',
          moves: todayResult.attempts.length,
          matches: todayResult.won ? GRID_SIZE * GRID_SIZE / 2 : todayResult.attempts.length / 2
        }))
        clearPartialGameState()
      }
    } else {
      const partialState = loadPartialGameState()
      if (partialState) {
        // Migrar cartas antiguas que usan 'value' a 'iconIndex'
        const cards = (partialState.cards || []).map((card: LegacyCard): Card => ({
          ...card,
          iconIndex: card.iconIndex !== undefined ? card.iconIndex : (card.value !== undefined ? card.value : 0)
        }))
        setGameState(prev => ({
          ...prev,
          cards,
          flippedCards: partialState.flippedCards || [],
          moves: partialState.moves || 0,
          matches: partialState.matches || 0,
          gameStatus: partialState.gameStatus === 'ready' ? 'playing' : (partialState.gameStatus || 'playing')
        }))
      } else {
        const cards = generateDailyCards(language)
        setGameState(prev => ({ ...prev, cards, gameStatus: 'playing' }))
      }
    }

    const hasSeenInstructions = localStorage.getItem(`game-memoria-de-pares-${language}-instructions`)
    if (!hasSeenInstructions && window.innerWidth < 1024) {
      setShowFirstTimeModal(true)
    }

    setIsInitialLoadComplete(true)
  }, [language, today])

  useEffect(() => {
    if (!isInitialLoadComplete) return
    if (gameState.gameStatus === 'playing' || gameState.gameStatus === 'ready') {
      savePartialGameState(gameState)
    }
  }, [gameState, isInitialLoadComplete])

  // Protección contra bloqueos: si isProcessing está activo por más de 5 segundos, resetearlo
  useEffect(() => {
    if (gameState.isProcessing && gameState.gameStatus === 'playing') {
      const timeout = setTimeout(() => {
        console.warn('Game stuck in processing state, resetting...')
        setGameState(prev => ({
          ...prev,
          isProcessing: false,
          flippedCards: []
        }))
      }, 5000) // 5 segundos

      return () => clearTimeout(timeout)
    }
  }, [gameState.isProcessing, gameState.gameStatus])

  const handleCardClick = useCallback((cardId: number) => {
    // No permitir clics si se está procesando o si ya hay 2 cartas volteadas
    if (gameState.isProcessing || gameState.gameStatus !== 'playing') return
    if (gameState.flippedCards.length >= 2) return
    if (gameState.flippedCards.includes(cardId)) return

    const card = gameState.cards[cardId]
    if (card.isMatched || card.isFlipped) return

    const newFlippedCards = [...gameState.flippedCards, cardId]
    const newCards = gameState.cards.map(c => 
      c.id === cardId ? { ...c, isFlipped: true } : c
    )

    // Si ahora hay 2 cartas volteadas, activar procesamiento inmediatamente
    const willHaveTwoCards = newFlippedCards.length === 2

    setGameState(prev => ({
      ...prev,
      cards: newCards,
      flippedCards: newFlippedCards,
      isProcessing: willHaveTwoCards
    }))

    // Si hay dos cartas volteadas, verificar si coinciden
    if (newFlippedCards.length === 2) {
      const [firstId, secondId] = newFlippedCards
      const firstCard = newCards.find(c => c.id === firstId)!
      const secondCard = newCards.find(c => c.id === secondId)!
      const firstIconIndex = firstCard.iconIndex
      const secondIconIndex = secondCard.iconIndex

      // Esperar un momento para que el usuario vea las cartas antes de verificar (1.5 segundos)
      setTimeout(() => {
        setGameState(prev => {
          // Verificar que las cartas aún están volteadas y obtener sus valores actuales
          const currentFirstCard = prev.cards.find(c => c.id === firstId)
          const currentSecondCard = prev.cards.find(c => c.id === secondId)
          
          if (!currentFirstCard || !currentSecondCard) {
            // Las cartas ya no existen o fueron modificadas, resetear estado
            return {
              ...prev,
              isProcessing: false
            }
          }

          if (firstIconIndex === secondIconIndex) {
            // Match! - Las cartas coinciden, marcarlas como emparejadas
            const updatedCards = prev.cards.map(c => 
              (c.id === firstId || c.id === secondId) ? { ...c, isMatched: true, isFlipped: true } : c
            )
            const newMatches = prev.matches + 1
            const newMoves = prev.moves + 1

            // Check win condition
            if (newMatches === GRID_SIZE * GRID_SIZE / 2 && prev.gameStatus !== 'won') {
              saveGameResult(`memoria-de-pares-${language}`, Array(newMoves).fill('move'), true, today)
              clearPartialGameState()
              if (!hasShownWinToastRef.current) {
                toast.success(t(`games.memoriaDePares.congratulations`))
                hasShownWinToastRef.current = true
              }
              setStats(getUserStats(`memoria-de-pares-${language}`))
              return {
                ...prev,
                cards: updatedCards,
                flippedCards: [],
                matches: newMatches,
                moves: newMoves,
                gameStatus: 'won' as const,
                isProcessing: false
              }
            }

            // Check lose condition
            if (newMoves >= MAX_MOVES) {
              saveGameResult(`memoria-de-pares-${language}`, Array(newMoves).fill('move'), false, today)
              clearPartialGameState()
              toast.error(t(`games.memoriaDePares.gameOver`))
              setStats(getUserStats(`memoria-de-pares-${language}`))
              return {
                ...prev,
                cards: updatedCards,
                flippedCards: [],
                matches: newMatches,
                moves: newMoves,
                gameStatus: 'lost' as const,
                isProcessing: false
              }
            }

            return {
              ...prev,
              cards: updatedCards,
              flippedCards: [],
              matches: newMatches,
              moves: newMoves,
              isProcessing: false
            }
          } else {
            // No match - voltear las cartas de nuevo
            const updatedCards = prev.cards.map(c => 
              (c.id === firstId || c.id === secondId) ? { ...c, isFlipped: false } : c
            )
            const newMoves = prev.moves + 1

            // Check lose condition
            if (newMoves >= MAX_MOVES) {
              saveGameResult(`memoria-de-pares-${language}`, Array(newMoves).fill('move'), false, today)
              clearPartialGameState()
              toast.error(t(`games.memoriaDePares.gameOver`))
              setStats(getUserStats(`memoria-de-pares-${language}`))
              return {
                ...prev,
                cards: updatedCards,
                flippedCards: [],
                moves: newMoves,
                gameStatus: 'lost' as const,
                isProcessing: false
              }
            }

            return {
              ...prev,
              cards: updatedCards,
              flippedCards: [],
              moves: newMoves,
              isProcessing: false
            }
          }
        })
      }, 1500) // Mostrar las cartas por 1.5 segundos antes de verificar/voltear
    } else {
      // Solo una carta volteada, permitir más clics
      setGameState(prev => ({ ...prev, isProcessing: false }))
    }
  }, [gameState, language, today, t])

  const handleCloseFirstTimeModal = () => {
    setShowFirstTimeModal(false)
    localStorage.setItem(`game-memoria-de-pares-${language}-instructions`, 'true')
  }

  const renderGameContent = () => (
    <>
      {/* Game Status Alert */}
      {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') && (
        <GameStatusAlert status={gameState.gameStatus}>
          <div className="space-y-1">
            <div className="font-bold">
              {gameState.gameStatus === 'won'
                ? t(`games.memoriaDePares.youWon`, { moves: gameState.moves })
                : t(`games.memoriaDePares.youLost`)
              }
            </div>
            <div className="text-xs font-medium">
              {t(`games.memoriaDePares.comeBackTomorrow`)}
            </div>
          </div>
        </GameStatusAlert>
      )}

      {/* Game Board */}
      <div className="space-y-6">
        {/* Game Info */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">{t(`games.memoriaDePares.moves`)}</div>
              <div className="text-xl font-bold">{gameState.moves}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t(`games.memoriaDePares.matches`)}</div>
              <div className="text-xl font-bold">{gameState.matches} / {GRID_SIZE * GRID_SIZE / 2}</div>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        {gameState.cards.length > 0 && (
          <div className="grid grid-cols-4 gap-2 lg:gap-3 max-w-md mx-auto">
            {gameState.cards.map((card) => (
              <button
                key={card.id}
                onClick={() => handleCardClick(card.id)}
                disabled={gameState.isProcessing || card.isMatched}
                className={`
                  aspect-square rounded-lg transition-all duration-300
                  ${card.isFlipped || card.isMatched
                    ? 'bg-primary'
                    : 'bg-muted hover:bg-muted/80'
                  }
                  ${card.isMatched ? 'opacity-50' : ''}
                  ${gameState.isProcessing ? 'cursor-wait' : 'cursor-pointer'}
                  flex items-center justify-center
                `}
              >
                {card.isFlipped || card.isMatched ? (
                  (() => {
                    const iconIndex = card.iconIndex
                    
                    if (iconIndex < 0 || iconIndex >= icons.length) {
                      return <span className="text-xl lg:text-2xl text-foreground">?</span>
                    }
                    
                    const IconComponent = icons[iconIndex]
                    if (!IconComponent) {
                      return <span className="text-xl lg:text-2xl text-foreground">?</span>
                    }
                    
                    return <IconComponent className="h-6 w-6 lg:h-8 lg:w-8 text-primary-foreground stroke-2" />
                  })()
                ) : (
                  <span className="text-xl lg:text-2xl font-bold text-foreground">?</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  )

  return (
    <GameLayout>
      {/* Desktop Layout */}
      <div className="hidden lg:flex lg:flex-row lg:gap-8 lg:px-8 lg:py-8">
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className="w-full max-w-xl xl:max-w-2xl">
            {renderGameContent()}
          </div>
        </div>

        {/* Sidebar - Desktop only */}
        <div className="w-80 min-w-80 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Copy className="h-4 w-4" />
                {t(`games.memoriaDePares.howToPlay`)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs">{t(`games.memoriaDePares.instructions`)}</p>
            </CardContent>
          </Card>

          <InContentAd className="w-full" compact />

          <GameShortcuts currentGame="memoria-de-pares" />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden min-h-[calc(100dvh-64px)] flex flex-col">
        <InContentAd className="w-full" compact />

        <div className="flex-1 flex flex-col py-2 overflow-y-auto">
          <div className="w-full py-4 space-y-4">
            {renderGameContent()}

            {/* Statistics */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">{t(`games.memoriaDePares.played`)}</div>
                    <div className="text-2xl font-bold">{stats.totalGamesPlayed}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">{t(`games.memoriaDePares.won`)}</div>
                    <div className="text-2xl font-bold">{stats.totalGamesWon}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">{t(`games.memoriaDePares.winRate`)}</div>
                    <div className="text-2xl font-bold">
                      {stats.totalGamesPlayed > 0 ? Math.round((stats.totalGamesWon / stats.totalGamesPlayed) * 100) : 0}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">{t(`games.memoriaDePares.maxStreak`)}</div>
                    <div className="text-2xl font-bold">{stats.maxStreak}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost' || hasPlayedToday(`memoria-de-pares-${language}`, today)) && (
              <GameShortcuts currentGame="memoria-de-pares" />
            )}
          </div>
        </div>
      </div>

      {/* Mobile First Time Modal */}
      <Dialog open={showFirstTimeModal} onOpenChange={setShowFirstTimeModal}>
        <DialogContent className="max-w-sm mx-auto mt-20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              {t(`games.memoriaDePares.howToPlay`)}
            </DialogTitle>
            <DialogDescription className="text-left space-y-3">
              <p className="text-sm">{t(`games.memoriaDePares.instructions`)}</p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={handleCloseFirstTimeModal}>
              {t("navigation.gotIt")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </GameLayout>
  )
}
